import classNames from "classnames";
import SettingHeader from "../Settings/SettingHeader";
import { useEffect, useState } from "react";
import Toast from "../Toast";
import TicketListItem from "./TicketListItem";
import SupportEmptyState from "./SupportEmptyState";
import useJiraTicketSearch, { JiraTicket } from "./services/useJiraTicketSearch";
import Loader from "../Loader";
import AwesomeIcon from "../AwesomeIcon";
import { faCommentAltExclamation } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";

interface SupportProps {
  pageName: string;
  isModal?: boolean;
  /** When true (Support modal visible), Jira search is refetched on every open */
  modalOpen?: boolean;
}

const Support = ({ pageName, isModal, modalOpen }: SupportProps) => {
  const { t } = useTranslation();
  const {
    data: searchResults,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useJiraTicketSearch(true);
  const isRefreshing = isFetching && !isLoading;
  const [expandedTicketKey, setExpandedTicketKey] = useState<string | null>(null);
  const tickets: JiraTicket[] = searchResults?.issues || [];

  /**
   * Modal: show centered Loader whenever the modal is open and Jira search is in flight (open + refetch).
   * Settings page: Loader only for first load or refetch while the list is still empty.
   */
  const showListLoader = isModal
    ? Boolean(modalOpen && isFetching)
    : isLoading || (isFetching && !isLoading && tickets.length === 0);

  useEffect(() => {
    if (!isModal || !modalOpen) return;
    void refetch();
  }, [isModal, modalOpen, refetch]);

  const toggleTicket = (ticketKey: string) => {
    setExpandedTicketKey((prev) => (prev === ticketKey ? null : ticketKey));
  };

  return (
    <>
      {!isModal && <SettingHeader pageTitle={pageName} />}
      <div
        className={classNames(
          "flex flex-col flex-1 min-h-0 overflow-auto w-full",
          isModal
            ? tickets.length === 0 && !showListLoader
              ? "pt-0 px-0 pb-0 bg-[#d9dbdd] dark:bg-[#0c1723]"
              : "pt-6 px-[25px] pb-5 bg-[#d9dbdd] dark:bg-[#0c1723]"
            : "p-4"
        )}
      >
        {isModal && !showListLoader && tickets.length > 0 && (
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex gap-3 items-start min-w-0">
              <div className="flex shrink-0 w-[40px] h-[40px] items-center justify-center text-[#474f58] dark:text-[#bbbfc2]">
                <AwesomeIcon
                  name={faCommentAltExclamation}
                  className="w-[40px] h-[40px]"
                />
              </div>
              <div className="flex flex-col items-start min-w-0 text-left text-[#474f58] dark:text-[#bbbfc2]">
                <p className="font-sans font-medium text-lg tracking-normal">
                  {t("support.track_complaints", {
                    defaultValue: "Track your complaints here",
                  })}
                </p>
                <p className="font-sans font-normal text-md tracking-normal">
                  {t("support.prompt_response", {
                    defaultValue: "We ensure prompt response.",
                  })}
                </p>
              </div>
            </div>
            <button
              onClick={() => refetch()}
              disabled={isRefreshing}
              className="flex items-center gap-3 shrink-0 bg-[#008280] hover:bg-[#006b6b] disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-3 rounded-8 text-base font-normal transition-colors"
            >
              {isRefreshing && (
                <Loader
                  size="small"
                  containerClassName="!py-0 !gap-0"
                  className="!border-gray-100 !border-r-transparent"
                />
              )}
              {isRefreshing
                ? t("support.refreshing", { defaultValue: "Refreshing…" })
                : t("support.refresh")}
            </button>
          </div>
        )}
        {!isModal && (
          <div className="space-y-3 mb-5">
            <div className="flex items-center justify-between">
              <h3 className="flex text-gray-800 dark:text-gray-100 text-xl font-semibold">
                {t("support.title")}
              </h3>
              <button
                onClick={() => refetch()}
                disabled={isRefreshing}
                className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium ml-auto transition-colors"
              >
                {isRefreshing && (
                  <Loader
                    size="small"
                    containerClassName="!py-0 !gap-0"
                    className="!border-white !border-r-transparent"
                  />
                )}
                {isRefreshing
                  ? t("support.refreshing", { defaultValue: "Refreshing…" })
                  : t("support.refresh")}
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div
            className={classNames(
              "rounded-8 p-4 mb-4 border",
              isModal
                ? "bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-500/40"
                : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
            )}
          >
            <p
              className={classNames(
                isModal
                  ? "text-red-800 dark:text-red-200"
                  : "text-red-800 dark:text-red-200"
              )}
            >
              {t("support.failed_to_load_tickets")}: {error.message}
            </p>
          </div>
        )}

        {/* Tickets list, empty state, or shared Loader (matches settings / patients pages) */}
        <div
          className={classNames(
            "space-y-3",
            isModal && "mt-0",
            isModal &&
              !showListLoader &&
              tickets.length === 0 &&
              "flex flex-1 flex-col min-h-0"
          )}
        >
          {showListLoader ? (
            <div
              className={classNames(
                "flex flex-1 min-h-[200px] items-center justify-center py-12",
                isModal && "min-h-[280px]"
              )}
            >
              <Loader
                size="medium"
                label={`${t("loading_key")}...`}
                containerClassName={classNames(
                  isModal && "!text-[#474f58] dark:!text-gray-300"
                )}
                className={
                  isModal
                    ? "!border-[#81878d] !border-r-transparent dark:!border-gray-400 dark:!border-r-transparent"
                    : undefined
                }
              />
            </div>
          ) : tickets.length === 0 ? (
            <SupportEmptyState isDark={!isModal} isModal={!!isModal} />
          ) : (
            tickets.map((ticket) => (
              <TicketListItem
                key={ticket.key}
                ticketKey={ticket.key}
                isExpanded={expandedTicketKey === ticket.key}
                onToggle={() => toggleTicket(ticket.key)}
                onRemove={() => {
                  Toast.error(t("support.tickets_auto_fetched"));
                }}
                variant={isModal ? "dark" : "default"}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default Support;
